import { CommonModule } from "@angular/common";
import { ChangeDetectorRef, Component, DestroyRef, OnInit, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Router } from "@angular/router";
import { MatIcon } from "@angular/material/icon";
import {
  DashboardGraphItem,
  DashboardKpi,
  GRAPH_COLORS,
  PERIODOS_DASHBOARD,
  PeriodoDashboard,
  RangoPeriodo,
  buildDonutStyle,
  normalizarGraficas,
  obtenerRangoPeriodo,
  porcentaje,
} from "src/app/dashboard/shared/dashboard-graficas";
import { PeriodoDashboardComponent } from "src/app/dashboard/shared/periodo-dashboard/periodo-dashboard.component";
import { PestanaDashboard } from "src/app/dashboard/shared/pestana-dashboard";
import { Graficas } from "src/app/graficas/gestionGraficas/graficas.model";
import { tieneAccesoMenu } from "src/app/shared/menu/acceso-menu";
import { AuthService } from "src/app/security/auth.service";
import { IaDashboard, IaDashboardService, SolicitudReciente, iaDashboardVacio } from "./ia-dashboard.service";

/**
 * Pestaña "IA" del Dashboard de Inicio: gobernanza ISO/IEC 42001, no
 * consumo de modelos (no existe). Inventario de activos IA, solicitudes
 * IA, incidencias IA, monitoreo continuo y evaluaciones de proveedores.
 * La matriz de riesgos IA vive en la pestaña Riesgos.
 *
 * Un solo endpoint devuelve el resumen; el periodo aplica a los FLUJOS
 * (solicitudes recibidas, dictamenes, incidencias abiertas en el periodo,
 * tendencias, activos con incidencias). El inventario, los backlogs
 * abiertos, el monitoreo y la vigencia de proveedores son FOTO y cada
 * tarjeta lo dice en su nota.
 *
 * Gating por hoja: la tarjeta de proveedores solo con
 * admin/evaluaciones-ia y la tabla de ultimas solicitudes solo con
 * admin/solicitudes-ia/gestion (el backend tambien los ata a esa hoja).
 * Consulta al montarse; el cascaron (dashini) la vuelve a consultar con
 * actualizar().
 */
@Component({
  selector: "app-dashboard-ia",
  standalone: true,
  templateUrl: "./dashboard-ia.component.html",
  styleUrls: ["../../shared/dashboard-tarjetas.css", "./dashboard-ia.component.css"],
  imports: [CommonModule, MatIcon, PeriodoDashboardComponent],
})
export class DashboardIaComponent implements OnInit, PestanaDashboard {
  readonly periodos: PeriodoDashboard[] = PERIODOS_DASHBOARD;
  readonly periodoInicial = "30";

  rango: RangoPeriodo = obtenerRangoPeriodo(this.periodoInicial);
  errorCarga = "";
  resumen: IaDashboard = iaDashboardVacio();

  kpis: DashboardKpi[] = [];
  solicitudEstatusItems: DashboardGraphItem[] = [];
  incidenciaEstadoItems: DashboardGraphItem[] = [];
  gravedadItems: DashboardGraphItem[] = [];
  solicitudMesItems: DashboardGraphItem[] = [];
  incidenciaMesItems: DashboardGraphItem[] = [];
  cicloVidaItems: DashboardGraphItem[] = [];
  activosIncidenciaItems: DashboardGraphItem[] = [];
  solicitudesRecientes: SolicitudReciente[] = [];

  totalSolicitudes = 0;
  totalIncidenciasPeriodo = 0;
  totalGravedad = 0;
  totalCicloVida = 0;
  totalActivosIncidencias = 0;
  solicitudMesMax = 0;
  incidenciaMesMax = 0;

  puedeVerProveedores = false;
  puedeGestionarSolicitudes = false;
  puedeVerInventario = false;
  puedeVerIncidencias = false;

  private cargandoDatos = false;
  private readonly destroyRef = inject(DestroyRef);
  /** true desde que la pestaña se destruye; guarda el repintado (ver repintar()). */
  private destruido = false;

  /** Estatus de SolicitudIABuss. */
  private readonly solicitudColors: Record<string, string> = {
    SOLICITADA: "#1565c0",
    EN_EVALUACION: "#ef6c00",
    INFORMACION_REQUERIDA: "#6a5acd",
    APROBADA: "#00897b",
    RECHAZADA: "#d32f2f",
    CANCELADA: "#8d8d8d",
  };

  /** Columnas del kanban de incidencias IA (incidencias-ia.constants). */
  private readonly incidenciaColors: Record<string, string> = {
    ABIERTO: "#b71c1c",
    PROCESO: "#ef6c00",
    ATENDIDO: "#1976d2",
    VOBO: "#2e7d32",
  };

  private readonly gravedadColors: Record<string, string> = {
    CRITICO: "#d32f2f",
    MEDIO: "#ef6c00",
    BAJO: "#00897b",
  };

  constructor(
    private iaDashboardService: IaDashboardService,
    private authService: AuthService,
    private router: Router,
    // La vista NO se repinta sola cuando llega la respuesta HTTP: en este
    // arranque zone.js no queda parcheando XMLHttpRequest, asi que el
    // estado cambia y el DOM se queda en "Actualizando indicadores..."
    // hasta el siguiente clic. Se pide el repintado a mano, el mismo
    // patron que usa el resto de la casa (asistencia/mi-asistencia).
    // NO quitar por "innecesario".
    private cdr: ChangeDetectorRef,
  ) {
    // Se registra ANTES de cualquier suscripcion: los callbacks de
    // DestroyRef corren en orden de registro, asi que cuando
    // takeUntilDestroyed cierre el stream al destruir la pestaña esto ya
    // vale true.
    this.destroyRef.onDestroy(() => (this.destruido = true));
  }

  ngOnInit() {
    const menus = this.authService.getMenuList();
    this.puedeVerProveedores = tieneAccesoMenu(menus, "admin/evaluaciones-ia");
    this.puedeGestionarSolicitudes = tieneAccesoMenu(menus, "admin/solicitudes-ia/gestion");
    this.puedeVerInventario =
      tieneAccesoMenu(menus, "admin/inventario-ia/gestion") || tieneAccesoMenu(menus, "admin/inventario-ia/consulta");
    this.puedeVerIncidencias = tieneAccesoMenu(menus, "INCIDENCIAS/INC_IA");
    this.consultarDashboard();
  }

  get cargando(): boolean {
    return this.cargandoDatos;
  }

  actualizar(): void {
    this.consultarDashboard();
  }

  /** La barra de periodo no emite al iniciar: el rango inicial ya se calculo arriba. */
  onPeriodo(rango: RangoPeriodo): void {
    this.rango = rango;
    this.consultarDashboard();
  }

  consultarDashboard(): void {
    this.cargandoDatos = true;
    this.errorCarga = "";

    this.iaDashboardService
      .dashboard({ fechaIni: this.rango.inicio, fechaFin: this.rango.fin })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.pintar(res || iaDashboardVacio());
          this.cargandoDatos = false;
          this.repintar();
        },
        error: (e) => {
          console.error("Error al cargar dashboard de IA:", e);
          this.pintar(iaDashboardVacio());
          this.errorCarga = "No fue posible cargar la informacion de gobernanza de IA.";
          this.cargandoDatos = false;
          this.repintar();
        },
      });
  }

  /**
   * Repinta la pestaña. detectChanges() sobre una vista ya destruida
   * truena; aqui takeUntilDestroyed corta la suscripcion al destruir, pero
   * el guardia deja el camino cerrado igual que en las demas pestañas.
   */
  private repintar(): void {
    if (this.destruido) {
      return;
    }
    // markForCheck ADEMAS de detectChanges: detectChanges refresca solo
    // ESTA vista, y el cascaron (dashini) lee el `cargando` de la pestaña
    // para habilitar su boton "Actualizar". Sin marcar el camino hacia la
    // raiz ese enlace se queda con el valor viejo: el boton se queda
    // deshabilitado para siempre y en desarrollo sale un NG0100.
    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }

  getBarPercent(item: DashboardGraphItem, total: number): number {
    return porcentaje(item.valor, total);
  }

  getBarStyle(item: DashboardGraphItem, index: number, total: number): Record<string, string> {
    return {
      width: `${this.getBarPercent(item, total)}%`,
      background: item.color || GRAPH_COLORS[index % GRAPH_COLORS.length],
    };
  }

  getSolicitudDonutStyle(): Record<string, string> {
    if (this.totalSolicitudes <= 0 || this.solicitudEstatusItems.length === 0) {
      return { background: "#eef1f5" };
    }

    return buildDonutStyle(this.solicitudEstatusItems, this.totalSolicitudes);
  }

  getIncidenciaDonutStyle(): Record<string, string> {
    if (this.totalIncidenciasPeriodo <= 0 || this.incidenciaEstadoItems.length === 0) {
      return { background: "#eef1f5" };
    }

    return buildDonutStyle(this.incidenciaEstadoItems, this.totalIncidenciasPeriodo);
  }

  /** Reusa los colores de estatus de la tabla de tickets: abierto, proceso, atendido/vobo, cancelado. */
  getSolicitudEstatusClass(solicitud: SolicitudReciente): string {
    switch (String(solicitud?.estatus || "").toUpperCase()) {
      case "SOLICITADA":
        return "ticket-status--abierta";
      case "EN_EVALUACION":
      case "INFORMACION_REQUERIDA":
        return "ticket-status--proceso";
      case "APROBADA":
        return "ticket-status--atendida";
      case "RECHAZADA":
        return "ticket-status--reabierta";
      default:
        return "ticket-status--cancelada";
    }
  }

  getSolicitudEstatusLabel(solicitud: SolicitudReciente): string {
    return String(solicitud?.estatus || "SIN ESTATUS").replaceAll("_", " ");
  }

  abrirInventario(): void {
    if (this.puedeVerInventario) {
      this.router.navigate(["/admin/inventario-ia/gestion"]);
    }
  }

  abrirIncidencias(): void {
    if (this.puedeVerIncidencias) {
      this.router.navigate(["/INCIDENCIAS/INC_IA"]);
    }
  }

  /** La gestion de solicitudes es un tablero: no hay ruta por solicitud, se abre el tablero. */
  abrirSolicitudes(): void {
    if (this.puedeGestionarSolicitudes) {
      this.router.navigate(["/admin/solicitudes-ia/gestion"]);
    }
  }

  private pintar(res: IaDashboard): void {
    this.resumen = res;
    this.solicitudEstatusItems = this.colorear(normalizarGraficas(res.solicitudesPorEstatus || []), this.solicitudColors);
    this.incidenciaEstadoItems = this.colorear(normalizarGraficas(res.incidenciasPorEstado || []), this.incidenciaColors);
    this.gravedadItems = this.colorear(normalizarGraficas(res.incidenciasPorGravedad || []), this.gravedadColors);
    this.solicitudMesItems = normalizarGraficas(res.solicitudesPorMes || [], false).slice(-8);
    this.incidenciaMesItems = normalizarGraficas(res.incidenciasPorMes || [], false).slice(-8);
    this.cicloVidaItems = this.enOrdenDeLlegada(res.inventarioPorCicloVida || []);
    this.activosIncidenciaItems = normalizarGraficas(res.activosConIncidencias || []).slice(0, 5);
    this.solicitudesRecientes = (res.solicitudesRecientes || []).slice(0, 5);

    this.totalSolicitudes = this.sumar(this.solicitudEstatusItems);
    this.totalIncidenciasPeriodo = this.sumar(this.incidenciaEstadoItems);
    this.totalGravedad = this.sumar(this.gravedadItems);
    this.totalCicloVida = this.sumar(this.cicloVidaItems);
    this.totalActivosIncidencias = this.sumar(this.activosIncidenciaItems);
    this.solicitudMesMax = Math.max(...this.solicitudMesItems.map((item) => item.valor), 0);
    this.incidenciaMesMax = Math.max(...this.incidenciaMesItems.map((item) => item.valor), 0);

    this.kpis = this.construirKpis(res);
  }

  private construirKpis(res: IaDashboard): DashboardKpi[] {
    const dictamenes = res.aprobadas + res.rechazadas;
    const horas = Math.round(res.horasADictamenPromedio || 0);
    const kpis: DashboardKpi[] = [
      {
        label: "Activos IA",
        value: res.activosActivos,
        meta: `${res.activosInactivos} inactivos - foto actual`,
        icon: "smart_toy",
        className: "ticket-kpi--total",
      },
      {
        label: "Solicitudes pendientes",
        value: res.solicitudesPendientes,
        meta: `${res.solicitudesSinResponsable} sin responsable - foto actual`,
        icon: "pending_actions",
        className: "ticket-kpi--active",
      },
      {
        label: "Tasa de aprobacion",
        value: `${porcentaje(res.aprobadas, dictamenes)}%`,
        meta: dictamenes > 0
          ? `${res.aprobadas} de ${dictamenes} dictamenes - ${horas} h a dictamen`
          : "Sin dictamenes en el periodo",
        icon: "verified",
        className: "ticket-kpi--done",
      },
      {
        label: "Incidencias IA abiertas",
        value: res.incidenciasAbiertas,
        meta: `${res.incidenciasCriticas} criticas - foto actual`,
        icon: "report_problem",
        className: "ticket-kpi--time",
      },
      {
        label: "Activos sin monitoreo",
        value: res.activosSinMonitoreo,
        meta: `Sin evaluacion en > ${res.diasSinMonitoreo || 120} dias`,
        icon: "monitor_heart",
        className: "ticket-kpi--muted",
      },
    ];

    if (this.puedeVerProveedores) {
      kpis.push({
        label: "Proveedores vigentes",
        value: res.proveedoresVigentes,
        meta: `${res.proveedoresVencidos} vencidos - foto actual`,
        icon: "fact_check",
        className: "ticket-kpi--active",
      });
    }

    return kpis;
  }

  /** Pone el color fijo de cada estatus; lo que no este en el mapa conserva el de la paleta. */
  private colorear(items: DashboardGraphItem[], colores: Record<string, string>): DashboardGraphItem[] {
    return items.map((item) => ({
      ...item,
      color: colores[item.nombre] || item.color,
    }));
  }

  /** El ciclo de vida ya viene en el orden del catalogo: se respeta en vez de ordenar por valor. */
  private enOrdenDeLlegada(items: Graficas[]): DashboardGraphItem[] {
    const porNombre = new Map(normalizarGraficas(items).map((item) => [item.nombre, item]));
    const ordenados: DashboardGraphItem[] = [];
    items.forEach((item) => {
      const nombre = String(item?.valor1 || "Sin clasificar").toUpperCase();
      const encontrado = porNombre.get(nombre);
      if (encontrado && !ordenados.includes(encontrado)) {
        ordenados.push(encontrado);
      }
    });
    return ordenados.map((item, index) => ({ ...item, color: GRAPH_COLORS[index % GRAPH_COLORS.length] }));
  }

  private sumar(items: DashboardGraphItem[]): number {
    return items.reduce((total, item) => total + item.valor, 0);
  }
}
